import type { FC } from "react"
import { useMemo } from "react"
import { Stack } from "@mui/material"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { FormProvider, useForm } from "react-hook-form"

import { selectCurrentOrgId } from "features/store/orgSlice"
import { useCreateUserMutation } from "features/api"
import { BRAND } from "features/config"
import { USER_ROLES, USER_ROLES_WITHOUT_OWNER } from "helpers/utils/constants"
import { buildGetErrorMessage, snackbarMutation } from "helpers/utils/mutations"
import usePermissions from "helpers/hooks/usePermissions"
import ControlledInput from "../common/ControlledInput"
import FormButtons from "../common/FormButtons"
import PopUp from "../common/PopUp"

interface ICreateUserPopupProps {
  open: boolean
  onClose: () => void
}

interface ICreateUserForm {
  username: string
  role: keyof typeof USER_ROLES
}

const CreateUserPopup: FC<ICreateUserPopupProps> = ({ onClose, open }) => {
  const { t } = useTranslation()
  const { isOwner } = usePermissions()
  const orgId = useSelector(selectCurrentOrgId)
  const methods = useForm<ICreateUserForm>({
    mode: "all",
    defaultValues: {
      username: "",
      role: "editor",
    },
  })
  const [createUser, { isLoading: createUserLoading }] = useCreateUserMutation()

  const ROLE_OPTIONS_BY_ROLE = useMemo(
    () => (isOwner ? USER_ROLES : USER_ROLES_WITHOUT_OWNER),
    [isOwner],
  )

  const roles = useMemo(
    () =>
      Object.entries(ROLE_OPTIONS_BY_ROLE).map(([key, value]) => ({
        value,
        label: t(`roles.${key.toUpperCase()}`, key),
      })),
    [t, ROLE_OPTIONS_BY_ROLE],
  )

  const onSubmit = ({ username, role }: ICreateUserForm) => {
    snackbarMutation({
      mutation: createUser({
        username,
        orgId: orgId as number,
        role,
        brand: BRAND,
      }).unwrap(),
      getErrorMessage: buildGetErrorMessage(
        t("error.CREATING_ITEM", { item: t("generic.USER") }),
      ),
      getSuccessMessage: () => t("users.USER_CREATED"),
    })
      .then(() => {
        onClose()
      })
      .catch(() => {})
  }

  return (
    <PopUp
      open={open}
      onClose={onClose}
      titleKey="enterprise.CREATE_USER"
      maxWidth={"sm"}
      fullWidth
    >
      <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
        <FormProvider {...methods}>
          <Stack mb={2}>
            <ControlledInput
              autoFocus
              name="username"
              type="email"
              placeholder={t("users.ENTER_USER_EMAIL")}
              label={t("generic.USERNAME")}
              rules={{
                required: t("generic.FIELD_REQUIRED"),
                pattern: {
                  value: /^\S+@\S+$/i,
                  message: t("generic.FIELD_INVALID_EMAIL"),
                },
              }}
              variant="outlined"
              disabled={createUserLoading}
            />
            <ControlledInput
              type="select"
              placeholder=""
              name="role"
              label={t("generic.ROLE")}
              selectOptions={roles}
              variant="outlined"
              disabled={createUserLoading}
            />
          </Stack>
          <FormButtons
            buttonText="generic.CREATE"
            onClose={onClose}
            disabled={createUserLoading}
            isLoading={createUserLoading}
          />
        </FormProvider>
      </form>
    </PopUp>
  )
}

export default CreateUserPopup
